import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Headline, BodyCopy } from '@xingternal/typography';
import { ModuleHeader } from '@xingternal/pages-common';
import { IconAddContact } from '@xingternal/icons';
import { Button } from '@xingternal/button';
import {
  getTrackingUrl,
  trackKununuRateCompanyClick,
  trackAllBenefitsClick,
} from '@xingternal/pages-tracking';

import { KununuData, KununuBenefits } from '../../types';
import styles from './Benefits.css';

type BenefitsCardProps = {
  type: KununuBenefits['type'];
  approvals: KununuBenefits['approvals'];
  totalReviews: number;
  percentage: number;
};

type BenefitsProps = Pick<
  KununuData,
  | 'benefits'
  | 'employerRatingsCount'
  | 'companyProfileUrl'
  | 'profileUuid'
  | 'evaluateProfileUrl'
>;

const TOP_BENEFITS_AMOUNT = 3;
const MINIMUM_BENEFIT_PERCENTAGE = 1;

const formatPercentage = (percentage: number) => `${Math.round(percentage)}%`;
const getTopBenefits = ({ benefits = [] }: { benefits: KununuBenefits[] }) =>
  [...benefits]
    .filter((benefit) => benefit.percentage >= MINIMUM_BENEFIT_PERCENTAGE)
    .sort((a, b) => a.approvals - b.approvals)
    .splice(-TOP_BENEFITS_AMOUNT)
    .reverse();

const BenefitsCard = ({
  type,
  approvals,
  totalReviews,
  percentage,
}: BenefitsCardProps) => (
  <div className={styles.benefitsCard} key={type}>
    <div>
      <Headline size="xlarge" as="strong">
        {formatPercentage(percentage)}
      </Headline>
    </div>
    <div>
      <Headline as="strong">
        <FormattedMessage
          id={`EP_BENEFIT_${type}`}
          defaultMessage={`EP_BENEFIT_${type}`}
        />
      </Headline>
      <BodyCopy size="small">
        <FormattedMessage
          id="EP_KUNUNU_BENEFITS_CONFIRMATION"
          defaultMessage="EP_KUNUNU_BENEFITS_CONFIRMATION"
          values={{ total: approvals, totalReviews }}
        />
      </BodyCopy>
    </div>
  </div>
);

const Benefits = ({
  benefits,
  companyProfileUrl,
  employerRatingsCount,
  profileUuid,
  evaluateProfileUrl,
}: BenefitsProps) => {
  const showMore = {
    href: `${getTrackingUrl({
      to: companyProfileUrl,
      trackingKey: 'viewKununuBenefits',
    })}#summary-benefits-card`,
    target: '_blank',
    copyKey: 'EP_KUNUNU_ALL_BENEFITS',
    number: benefits.length,
    externalLink: true,
    onClick: () => trackAllBenefitsClick(),
  };

  const topBenefits = getTopBenefits({
    benefits,
  });
  const showRateCompanyModule = profileUuid && evaluateProfileUrl;
  const showBenefitsModule = topBenefits.length > 0 || showRateCompanyModule;

  if (!showBenefitsModule) return null;

  return (
    <div className={styles.wrapper}>
      <ModuleHeader
        headlineCopyKey="EP_KUNUNU_BENEFITS_TITLE"
        small
        showMore={showMore}
      />
      {topBenefits.length > 0 && (
        <section data-cy="benefits_section" className={styles.benefitsSection}>
          {topBenefits.map(({ type, approvals, percentage }) => (
            <BenefitsCard
              key={type}
              type={type}
              approvals={approvals}
              totalReviews={employerRatingsCount}
              percentage={percentage}
            />
          ))}
        </section>
      )}
      {showRateCompanyModule && (
        <section className={styles.rateThisCompanyContainer}>
          <p>
            <FormattedMessage
              id="EP_KUNUNU_RATE_COMPANY"
              defaultMessage="EP_KUNUNU_RATE_COMPANY"
            />
          </p>
          <Button
            onClick={() => trackKununuRateCompanyClick()}
            variant="tertiary"
            size="small"
            href={getTrackingUrl({
              to: evaluateProfileUrl,
              trackingKey: 'rateCompany',
            })}
            target="_blank"
            icon={IconAddContact}
          >
            <FormattedMessage
              id="EP_KUNUNU_RATE_COMPANY_BUTTON"
              defaultMessage="EP_KUNUNU_RATE_COMPANY_BUTTON"
            />
          </Button>
        </section>
      )}
    </div>
  );
};

export default React.memo(Benefits);
